import { useEffect } from 'react';
import { useTourContext } from '@/store/tourContext';

interface UseManagePatientsTutorialProps {
	managePatientsTableRef: React.RefObject<HTMLDivElement | null>;
}

interface UseGraftsToAssignTutorialProps {
	graftsToAssignTableRef: React.RefObject<HTMLDivElement | null>;
}

interface UseConfirmedGraftsTutorialProps {
	confirmedGraftsTableRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Hook to register ManagePatientsTable ref for GraftLogs tutorial
 */
export const useManagePatientsTutorial = ({
	managePatientsTableRef,
}: UseManagePatientsTutorialProps): void => {
	const { registerElement } = useTourContext();

	useEffect(() => {
		const registerRef = () => {
			if (managePatientsTableRef.current) {
				registerElement('ManagePatientsTableRef', managePatientsTableRef.current);
			}
		};

		registerRef();
		const timeoutId1 = setTimeout(registerRef, 100);
		const timeoutId2 = setTimeout(registerRef, 500);
		const timeoutId3 = setTimeout(registerRef, 1000);
		const timeoutId4 = setTimeout(registerRef, 2000);

		const intervalId = setInterval(registerRef, 2000);

		return () => {
			clearTimeout(timeoutId1);
			clearTimeout(timeoutId2);
			clearTimeout(timeoutId3);
			clearTimeout(timeoutId4);
			clearInterval(intervalId);
			registerElement('ManagePatientsTableRef', null);
		};
	}, [registerElement, managePatientsTableRef]);
};

/**
 * Hook to register GraftsToAssignTable refs for GraftLogs tutorial
 */
export const useGraftsToAssignTutorial = ({
	graftsToAssignTableRef,
}: UseGraftsToAssignTutorialProps): void => {
	const { registerElement } = useTourContext();

	useEffect(() => {
		const registerTableRef = () => {
			if (graftsToAssignTableRef.current) {
				registerElement('GraftsToAssignTableRef', graftsToAssignTableRef.current);
			}
		};

		registerTableRef();
		const timeoutId1 = setTimeout(registerTableRef, 100);
		const timeoutId2 = setTimeout(registerTableRef, 500);
		const timeoutId3 = setTimeout(registerTableRef, 1000);
		const timeoutId4 = setTimeout(registerTableRef, 2000);

		const intervalId = setInterval(registerTableRef, 2000);

		return () => {
			clearTimeout(timeoutId1);
			clearTimeout(timeoutId2);
			clearTimeout(timeoutId3);
			clearTimeout(timeoutId4);
			clearInterval(intervalId);
			registerElement('GraftsToAssignTableRef', null);
		};
	}, [registerElement, graftsToAssignTableRef]);

	useEffect(() => {
		const registerButtonRef = () => {
			if (graftsToAssignTableRef.current) {
				// Look for the add/create button
				const addButton = graftsToAssignTableRef.current.querySelector(
					'button[title*="Add"], button[aria-label*="Add"], button[title*="Create"]',
				) as HTMLElement;
				if (addButton) {
					registerElement('addButtonGraftsToAssignTableRef', addButton);
				}
			}
		};

		const timeoutId1 = setTimeout(registerButtonRef, 500);
		const timeoutId2 = setTimeout(registerButtonRef, 1000);
		const timeoutId3 = setTimeout(registerButtonRef, 2000);
		const timeoutId4 = setTimeout(registerButtonRef, 3000);

		const intervalId = setInterval(registerButtonRef, 2000);

		return () => {
			clearTimeout(timeoutId1);
			clearTimeout(timeoutId2);
			clearTimeout(timeoutId3);
			clearTimeout(timeoutId4);
			clearInterval(intervalId);
			registerElement('addButtonGraftsToAssignTableRef', null);
		};
	}, [registerElement, graftsToAssignTableRef]);
};

/**
 * Hook to register ConfirmedGraftsTable ref for GraftLogs tutorial
 */
export const useConfirmedGraftsTutorial = ({
	confirmedGraftsTableRef,
}: UseConfirmedGraftsTutorialProps): void => {
	const { registerElement } = useTourContext();

	useEffect(() => {
		const registerRef = () => {
			if (confirmedGraftsTableRef.current) {
				registerElement('confirmedGraftsTableRef', confirmedGraftsTableRef.current);
			}
		};

		registerRef();

		const timeoutId1 = setTimeout(registerRef, 100);
		const timeoutId2 = setTimeout(registerRef, 500);
		const timeoutId3 = setTimeout(registerRef, 1000);
		const timeoutId4 = setTimeout(registerRef, 2000);

		const intervalId = setInterval(registerRef, 2000);

		return () => {
			clearTimeout(timeoutId1);
			clearTimeout(timeoutId2);
			clearTimeout(timeoutId3);
			clearTimeout(timeoutId4);
			clearInterval(intervalId);
			registerElement('confirmedGraftsTableRef', null);
		};
	}, [registerElement, confirmedGraftsTableRef]);
};
